/**
 * Store trade rules (Settings → Trade-ins) as the client sees them.
 *
 * The server owns every trade-in number: it prices lines that follow the trade's % with
 * `priceFollowingCustomerLine` in backend/src/services/business/trade-pricing.ts. The copy here
 * is only a PREVIEW (the add-item modal shows the value before the line exists) and must stay
 * identical; once a line is saved the page shows the value the server stored.
 *
 * Money: market and offer amounts are USD cents. The bulk floor is entered and stored in the
 * store currency (`bulkFloor.currency`); the server converts it and hands the USD version to the
 * trade page as `bulkFloorUsd` (GET /trades/rules), so nothing here converts currency.
 */

export type TradeRules = {
  cashPercent: number | null
  creditPercent: number | null
  bulkFloor: {
    enabled: boolean
    /** Market under this (store-currency cents) is bulk. */
    belowCents: number
    /** What a bulk card is worth (store-currency cents). */
    flatCents: number
    currency: string
  }
}

/** The floor in USD cents, as GET /trades/rules returns it (null when off or no exchange rate). */
export type BulkFloorUsd = { belowUsdCents: number; flatUsdCents: number }

/** Where a trade's "Customer % of Market" came from. */
export type CustomerPercentSource = 'rules' | 'manual'

export const defaultTradeRules = (storeCurrency: string): TradeRules => ({
  cashPercent: null,
  creditPercent: null,
  bulkFloor: { enabled: false, belowCents: 0, flatCents: 0, currency: storeCurrency.toUpperCase() },
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const readPercent = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100 ? value : null

const readCents = (value: unknown): number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0

/** Rules from `organization.metadata.tradeRules` (or GET /trades/rules), defaults when absent. */
export function parseTradeRules(raw: unknown, storeCurrency: string): TradeRules {
  const fallback = defaultTradeRules(storeCurrency)
  if (!isRecord(raw)) return fallback
  const floor = isRecord(raw.bulkFloor) ? raw.bulkFloor : {}
  return {
    cashPercent: readPercent(raw.cashPercent),
    creditPercent: readPercent(raw.creditPercent),
    bulkFloor: {
      enabled: floor.enabled === true,
      belowCents: readCents(floor.belowCents),
      flatCents: readCents(floor.flatCents),
      currency: typeof floor.currency === 'string' && /^[A-Za-z]{3}$/.test(floor.currency)
        ? floor.currency.toUpperCase()
        : fallback.bulkFloor.currency,
    },
  }
}

/** Store credit pays the credit %, every other payment method the cash %. */
export function rulePercentForPaymentMethod(rules: TradeRules | null | undefined, paymentMethod: string): number | null {
  if (!rules) return null
  return paymentMethod === 'store_credit' ? rules.creditPercent : rules.cashPercent
}

/** Mirror of the server's line pricing: flat floor under the threshold, else market × %. */
export function priceFollowingCustomerLine(
  marketUsdCents: number,
  percent: number,
  floor: BulkFloorUsd | null | undefined,
): { unitValueCents: number; floorApplied: boolean } {
  if (floor && floor.belowUsdCents > 0 && marketUsdCents < floor.belowUsdCents) {
    return { unitValueCents: floor.flatUsdCents, floorApplied: true }
  }
  return { unitValueCents: Math.max(0, Math.round((marketUsdCents * percent) / 100)), floorApplied: false }
}

/**
 * The % a trade should show on load: the saved % and its source when the draft has them; a % saved
 * before sources existed (Phase A) counts as typed; no saved % follows the store rule.
 * `rulePercent` is undefined while the rules are still loading — a rule-sourced draft then keeps
 * its saved % rather than showing blank (which would clear it on the server).
 */
export function initialTradePercent(
  metadata: Record<string, unknown> | null | undefined,
  rulePercent: number | null | undefined,
): { percent: number | null; source: CustomerPercentSource } {
  const saved = metadata?.customerPercentOfMarket
  const savedPercent = typeof saved === 'number' && Number.isFinite(saved) ? saved : null
  const source = metadata?.customerPercentSource
  if (source === 'manual') return { percent: savedPercent, source: 'manual' }
  if (source !== 'rules' && savedPercent !== null) return { percent: savedPercent, source: 'manual' }
  return { percent: rulePercent === undefined ? savedPercent : rulePercent, source: 'rules' }
}
